<script setup lang="ts">
import PostMessage from "~/components/ui/post/PostMessage.vue";
import PostAction from "~/components/ui/post/PostAction.vue";
import {type FeedPost, MediaType, PostScope} from "~/repository/types.d";
import PostAttachments from "~/components/ui/post/PostAttachments.vue";
import PostDocuments from "~/components/ui/post/PostDocuments.vue";
import {ref} from "vue";
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Keyboard, Navigation, Pagination} from 'swiper/modules';

//@TODO move to module. This can not be copy-pasted
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import {Tooltip} from "~/components/ui/tooltip";
import {useToast} from "~/components/ui/toast";
import {useSensitiveContent} from "~/composables/useSensitiveContent";

defineOptions({name: 'Post'});
const {t} = useI18n()
const {toast} = useToast();
const props = withDefaults(defineProps<{
  post: FeedPost,
  profileFeed?: boolean,
}>(), {
  profileFeed: false,
});

const emit = defineEmits<{
  'update:post': [payload: FeedPost],
  'delete:post': [payload: FeedPost]
}>();

const swiperConfig = {
  breakpoints: {
    '768': {
      spaceBetween: 0,
    }
  },
  modules: [Pagination, Keyboard, Navigation],
  pagination: {clickable: true, dynamicBullets: true},
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
}

const {APP_URL} = useRuntimeConfig().public;
const {user, isLoggedIn} = useAuthStore();

const {
  showSensitiveContent,
  canSeeSensitiveContentButton,
  shouldHideSensitiveContent
} = useSensitiveContent(props.post)

// @todo Fix composable usage. Composable should not be used as return result of a function
const getTaxCalculation = (price: number) => {
  return useTaxCalculation(price, 'post');
};

const {like} = usePostAPI(ref(props.post));
const loading = ref(false)
const router = useRouter()
const {locale} = useI18n();

const isMessageExpanded = ref(false);
const activeSlideIndex = ref(0);

function handleExpanded(expanded: boolean) {
  isMessageExpanded.value = expanded;
}

function updateActiveSlide(swiper) {
  activeSlideIndex.value = swiper.activeIndex;
  requestAnimationFrame(() => {
    document.body.offsetHeight;
  });
}

const {init: initPayment} = usePaymentStore()

async function buy() {
  if (!isLoggedIn) {
    throw createError({
      status: 401
    })
  }

  loading.value = true

  // Send add_to_cart event to GTM before initializing payment
  const {sendAddToCartPostEvent} = useEventLogger();
  sendAddToCartPostEvent(
    props.post.id,
    props.post.author.username,
    props.post.price.amount,
    props.post.price.currency_code
  );

  await initPayment(`buy/post/${props.post.id}`, {
    return_url: `${APP_URL}${router.resolve({
      name: 'posts-id-payment-processing',
      params: {
        id: props.post.id
      }
    }).fullPath.substring(1)}`
  }, new OneTimePayment(
    props.post.price.amount,
    props.post.author.username,
    props.post.price.currency_code
  ))
  loading.value = false
}

const selectedPlan = ref('month');
const showModal = ref(false);
const showDocumentsModal = ref(false);
const success_url = router.resolve({name: 'posts-id-subscribe-processing', params: {id: props.post.id}})

const closeModal = inject('closeModal')

async function initSubscription() {
  if (!isLoggedIn) {
    throw createError({
      status: 401
    })
  }
  showModal.value = false
  loading.value = true

  // Send add_to_cart event to GTM before initializing subscription payment
  const {sendAddToCartSubscriptionEvent} = useEventLogger();

  // Get subscription price based on selected plan
  const subscriptionPrice = selectedPlan.value === 'month' ?
    props.post.author.subscription.monthly :
    props.post.author.subscription.yearly;

  // Send the event
  sendAddToCartSubscriptionEvent(
    props.post.author.username,
    subscriptionPrice,
    props.post.author.subscription.currency_code
  );

  await initPayment(`subscribe/${props.post.author.username}`, {
    interval: selectedPlan.value,
    return_url: `${APP_URL}${success_url.fullPath.substring(1)}`
  }, new Subscribe(
    props.post.author
  ))
}


function handleDocumentsAccess() {
  if (!props.post.has_access) {
    toast({
      title: t('post.access_required'),
      description: t('post.unlock_documents_info'),
    });
    return;
  }

  showDocumentsModal.value = true;
}
</script>

<template>
  <div
    class="relative post-container overflow-hidden w-full h-full before:block before:absolute before:inset-0 before:content-[''] before:bg-gray-100 before:transition-all before:z-30 before:duration-300 before:md:rounded-2xl flex bg-gray-900 md:rounded-2xl will-change-transform transform-gpu"
    :class="{ 'before:opacity-100 ': isMessageExpanded, 'before:invisible before:opacity-0': !isMessageExpanded }">

    <div v-if="shouldHideSensitiveContent"
         class="absolute inset-0 z-[53] flex flex-col items-center justify-center background-shadow-xl backdrop-blur-2xl">
      <div class="text-center p-6 h-full w-full flex items-center justify-center flex-col">
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex justify-center items-center flex-col gap-6">
          <svg aria-label="Ukryte słowa" width="30px" height="40px" role="img" viewBox="0 0 24 24"
               fill="#ffffff"><title></title>
            <path
              d="M2.936 8.014A12.792 12.792 0 0 0 .559 11.82a1 1 0 0 0 1.881.677 10.987 10.987 0 0 1 1.988-3.15 1 1 0 1 0-1.492-1.332Zm20.271 13.779-5.916-5.916a4.969 4.969 0 0 0 .133-.582.983.983 0 0 0-1.107-1.108 3.315 3.315 0 0 1-.678.038l-3.366-3.366a3.3 3.3 0 0 1 .036-.676.99.99 0 0 0-1.134-1.107 4.623 4.623 0 0 0-.553.132L8.13 6.717a10.395 10.395 0 0 1 13.429 5.779 1 1 0 0 0 1.881-.677C23.413 11.74 20.542 4 12 4a12.104 12.104 0 0 0-5.367 1.22L2.207.792A1 1 0 0 0 .793 2.207l21 21a1 1 0 0 0 1.414-1.414ZM11.382 14.9l-3.044-3.03a1.005 1.005 0 0 0-1.636.326 5.495 5.495 0 0 0 1.31 6.074 5.495 5.495 0 0 0 6.075 1.31 1.005 1.005 0 0 0 .325-1.636Z"></path>
          </svg>
          <h3 class="text-white text-2xl font-semibold mb-2">{{ t('post.adult.title') }}</h3>
        </div>
        <!--        v-if="canSeeSensitiveContentButton"-->
        <Button variant="outline"
                @click="showSensitiveContent"
                class="bg-white mt-auto border-white flex items-center rounded-full">
          {{ t('post.adult.button') }}
        </Button>
      </div>
    </div>
    <PostAttachments :post="post" :attachments="post.attachments"/>

    <template v-if="post.media.length > 0">
      <PostMedia v-if="post.media.length < 2" :postId="post.id" :media="post.media[0]"/>
      <swiper
        v-else
        :direction="'horizontal'"
        :keyboard="true" :breakpoints="swiperConfig.breakpoints" :watchSlidesProgress="true"
        :modules="swiperConfig.modules" :pagination="swiperConfig.pagination"
        style="width: 100%; height: 100%;" :noSwipingSelector="'.post-message'"
        :touchReleaseOnEdges="true"
        :navigation="swiperConfig.navigation"
        @slideChange="updateActiveSlide">
        <swiper-slide v-for="(media, index) in post.media.filter(m => m.type !== MediaType.DOC)" :key="post.id"
                      class="h-full w-full will-change-transform transform-gpu">
          <PostMedia :media="media" :postId="post.id"/>
        </swiper-slide>
        <div class="swiper-button-prev relative p-2 post-buttons group">
              <span
                class="btn-bg absolute z-[-1] top-1/2 left-1/2 w-9 h-9 rounded-full bg-[#54545480]  group-hover:opacity-70 transform -translate-x-1/2 -translate-y-1/2 scale-100 transition-transform duration-150"></span>
          <IconArrowLeft size="20px" color="#fff"/>
        </div>
        <div class="swiper-button-next rotate-180 relative p-2 post-buttons group">
              <span
                class="btn-bg absolute z-[-1] top-1/2 left-1/2 w-9 h-9 rounded-full bg-[#54545480]  group-hover:opacity-70 transform -translate-x-1/2 -translate-y-1/2 scale-100 transition-transform duration-150"></span>
          <IconArrowLeft size="20px" color="#fff"/>
        </div>


      </swiper>
    </template>

    <template v-if="!post.has_access">
      <div class="flex items-end p-4 absolute  bottom-0 left-0 right-0">
        <div class="flex flex-row-reverse items-stretch justify-between grow relative z-30">
          <div class="flex flex-col">
            <button
              v-if="post.media.some(m => m.type === MediaType.DOC)"
              @click="handleDocumentsAccess"
              class="bg-white text-blue rounded-full p-2 mb-5 -ml-1"
              :class="{ 'opacity-50 cursor-not-allowed': !post.has_access }"
            >
              <Icon name="material-symbols:description" size="24px"/>
            </button>
            <TooltipProvider v-if="post.scope === PostScope.SUBSCRIBERS_ONLY && !post.is_subscribed">
              <Tooltip>
                <TooltipTrigger as-child>
                  <button @click="showModal = true"
                          class="bg-gradient-button rounded-full mb-5 -mr-2  p-2 font-semibold "
                  >
                    <IconCrown color="#ffffff"/>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{{ t('post.subscribe.tooltip') }}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <div class="relative self-end z-30 interactive-element force-render">
              <PostAction :post="post" @handle:like="like"/>
            </div>
          </div>
          <div
            class="mt-4 pr-4  space-y-2 grow flex flex-col justify-end items-stretch">
            <div class="flex shrink-0  items-center grow-0 justify-start interactive-element force-render">
              <PostAuthor v-show="post.author" :user="post.author"/>
              <div class="flex items-center" v-if="!user || post.author.id !== user.id">
              </div>
            </div>
            <div class="force-render mt-2">
              <PostMessage v-if="post.message" :text="post.message"
                           @expanded="handleExpanded($event)"/>
            </div>

            <template v-if="post.scope === PostScope.SUBSCRIBERS_ONLY && !post.is_subscribed">
              <Modal v-model="showModal">
                <template #dialog-content>
                  <DialogContent class="max-w-md">
                    <DialogHeader>
                      <button @click="showModal = false" class="w-auto text-left font-normal text-[17px]"
                              variant="ghost">
                        {{ t('buttons.Cancel') }}
                      </button>
                      <h2 class="text-center">{{ t('post.subscribe.modal_title') }}</h2>
                    </DialogHeader>
                    <SubscriptionSubscriberBenefitsModal @subscribe="initSubscription" :profile="post.author"/>
                  </DialogContent>
                </template>
                <template #drawer-content>

                  <DrawerContent class="min-h-auto h-auto max-h-dvh overflow-hidden">
                    <DrawerHeader>
                      {{ t('post.subscribe.modal_title') }}
                    </DrawerHeader>
                    <SubscriptionSubscriberBenefitsModal @subscribe="initSubscription" :profile="post.author"/>
                  </DrawerContent>
                </template>
              </Modal>
              <Button
                class="h-9 mt-2 text-white cursor-pointer bg-gradient-button opacity-100 hover:bg-opacity-100 transition-opacity relative duration-200"
                @click="initSubscription">
                {{ t('post.subscribe.button') }}
              </Button>
            </template>
            <Button :pending="loading" @click="buy" variant="default" v-else-if="post.price.amount !== 0"
                    class="h-9 text-primary-foreground cursor-pointer bg-gradient-button transition-all"
            >
              {{ t('post.pay_prefix') }} {{ getTaxCalculation(post.price.amount).total }}
              {{ post.price.currency_code.toUpperCase() }} {{ t('post.pay_suffix') }}
            </Button>

          </div>
        </div>
      </div>
    </template>

    <div class="flex items-end p-4 absolute  bottom-0 left-0 right-0"
         v-else>
      <div class="flex flex-col items-stretch justify-end mr-2 grow relative z-30">
        <div class="flex flex-col gap-3">
          <PostRepostedBy v-if="post.reposted_by" :reposted-by="post.reposted_by"/>
          <div class="flex shrink-0 items-center grow-0 justify-start interactive-element force-render">
            <PostAuthor v-if="post.author" :user="post.author"/>
            <div class="flex items-center" v-if="!user || post.author.id !== user.id">
              <span class="mx-1.5 text-white">•</span>
              <UserFollowButton :profile="post.author" variant="outline"/>
            </div>
          </div>
        </div>
        <div class="force-render mt-2">
          <PostMessage v-if="post.message" :text="post.message"
                       @expanded="handleExpanded($event)"/>
        </div>
      </div>
      <div class="relative interactive-element z-30  force-render">
        <button
          v-if="post.media.some(m => m.type === MediaType.DOC)"
          @click="showDocumentsModal = true"
          class="bg-white text-blue rounded-full p-2 mb-5 -ml-1"
        >
          <Icon name="material-symbols:description" size="24px"/>
        </button>
        <PostAction
          :post="post"
          @handle:like="like"
          @update:post="post = $event"
          @delete:post="$emit('delete:post', $event)"
        />
      </div>
    </div>
    <Modal v-model="showDocumentsModal">
      <template #dialog-content>
        <DialogContent class="max-w-md">
          <DialogHeader>
            <button @click="showDocumentsModal = false"
                    class="w-auto text-left font-normal text-[17px]" variant="ghost">
              {{ t('buttons.Cancel') }}
            </button>
            <h2 class="text-center">{{ t('post.attachments') }}</h2>
          </DialogHeader>
          <PostDocuments :documents="post.media.filter(m => m.type === MediaType.DOC)"/>
        </DialogContent>
      </template>

      <template #drawer-content>
        <DrawerContent class="min-h-auto h-auto max-h-dvh overflow-hidden">
          <DrawerHeader>
            {{ t('post.attachments') }}
          </DrawerHeader>
          <PostDocuments :documents="post.media.filter(m => m.type === MediaType.DOC)"/>
        </DrawerContent>
      </template>
    </Modal>

  </div>
</template>
<style>


.force-render {
  transform: translateZ(0);
  backface-visibility: hidden;
}
</style> 
